import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import useWhatsappCityHint from 'src/hooks/useWhatsappCityHint'
import { IPost } from 'src/types/hygraph'
import { formatTimestampMonthDay } from 'src/utils'
import { HygraphTopics, TOPIC_MAP } from 'src/utils/hygraph'
import { appendCityHint, buildWhatsappUrl } from 'src/utils/whatsapp'
import { RedirectUrls } from '../../types/types'
import LikedArticle from './LikedArticle'
import RelatedArticles from './RelatedArticles'
import RichTextContainer from './RichTextContainer'
import { headingsOf, stripTrailingRelated } from './toc'

type IProps = {
  topic: HygraphTopics
  post: IPost
}

const formatDate = (iso: string | undefined): string | null => {
  if (!iso) return null
  // Deterministic — was `toLocaleDateString(locale, …)` which diverges
  // between Node (build) and Chrome (client) on Spanish month names and
  // causes hydration #418 on /acerca-de-spotme/* SSG pages.
  return (
    formatTimestampMonthDay(iso, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }) || null
  )
}

const Article = ({ topic, post }: IProps) => {
  const { t } = useTranslation('common')
  const { title, coverImage, author, updatedAt, publishedAt } = post
  const { path, label } = TOPIC_MAP[topic]
  const dateString = formatDate(updatedAt ?? publishedAt)

  // The auto-generated related sidebar (structuredData.related, ≥3 items)
  // already carries the links a hand-written trailing "Documentos
  // relacionados" section repeats — drop the duplicate and build the
  // table of contents from what is actually rendered.
  const sd = Array.isArray(post.structuredData) ? post.structuredData : []
  const hasRelatedSidebar = sd.some(
    (entry) =>
      entry?.type === 'related' &&
      Array.isArray(entry.items) &&
      entry.items.length >= 3
  )
  const raw = hasRelatedSidebar
    ? stripTrailingRelated(post.content?.raw)
    : post.content?.raw
  const content = { ...post.content, raw }
  const toc = headingsOf(raw)
  const showToc = toc.length >= 3
  const tocList = (
    <ol className="mt-3 flex flex-col">
      {toc.map((item) => (
        <li key={item.id}>
          <a
            href={`#${item.id}`}
            className="block border-t border-ink-100/15 py-2 text-[14px] leading-snug text-ink-700 transition-colors hover:text-blue-500"
          >
            {item.text}
          </a>
        </li>
      ))}
    </ol>
  )
  const cityHintLine = useWhatsappCityHint()

  return (
    <>
      {/* Editorial header */}
      <section className="bg-cream-300 px-5 pt-[calc(var(--nav-h)+40px)] pb-12 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-16">
        <div className="mx-auto w-full max-w-4xl">
          {/* Breadcrumbs */}
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-display text-[12px] tracking-[0.06em] text-ink-200 md:mb-10 md:text-[13px]">
            <Link
              href={RedirectUrls.Help}
              className="transition-colors hover:text-blue-500"
            >
              {t('HELP.BREADCRUMB_HOME')}
            </Link>
            <span aria-hidden="true" className="text-ink-200/50">
              /
            </span>
            <Link href={path} className="transition-colors hover:text-blue-500">
              {label}
            </Link>
            <span aria-hidden="true" className="text-ink-200/50">
              /
            </span>
            <span className="line-clamp-1 text-ink-700">{title}</span>
          </nav>

          {/* Topic eyebrow */}
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {label}
          </p>

          {/* H1 */}
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-[1.08] font-medium tracking-[-0.01em] text-ink-700 md:mt-5 md:text-[56px]">
            {title}
          </h1>

          {/* Meta strip */}
          {(author?.name || dateString) && (
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-ink-100/15 pt-5 font-display text-[12px] tracking-[0.04em] text-ink-200 md:text-[13px]">
              {author?.name && (
                <span>
                  {`${t('HELP.ARTICLE_BY')} `}
                  <span className="text-ink-700">{author.name}</span>
                </span>
              )}
              {dateString && (
                <span>
                  {t('HELP.ARTICLE_UPDATED_ON', { date: dateString })}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Cover image */}
      {coverImage?.url && (
        <section className="bg-white-500 px-5 md:px-10">
          <div className="mx-auto w-full max-w-4xl">
            <div className="relative aspect-[16/8] w-full overflow-hidden border border-ink-100/15 bg-cream-300">
              <Image
                src={coverImage.url}
                alt={post.coverImageAlt ?? ''}
                priority
                fill
                sizes="(max-width: 899px) 100vw, 800px"
                quality={75}
                className="object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* Article body + sidebar */}
      <section className="bg-white-500 px-5 pt-12 pb-20 md:px-10 md:pt-16 md:pb-28">
        <div className="mx-auto w-full max-w-4xl">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
            <article className="min-w-0 md:col-span-8">
              {showToc && (
                <details className="mb-8 border-y border-ink-100/15 py-3 md:hidden">
                  <summary className="cursor-pointer font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase select-none">
                    {t('HELP.TOC_TITLE')}
                  </summary>
                  {tocList}
                </details>
              )}
              <RichTextContainer
                content={content}
                headingIds={toc.map((item) => item.id)}
              />
              <RelatedArticles post={post} className="mt-12 md:mt-16" />
              <div className="mt-12">
                <LikedArticle
                  title={title}
                  slug={post.slug}
                  topic={topic}
                  surface="help"
                />
              </div>
            </article>

            <aside className="md:col-span-4">
              <div className="md:sticky md:top-32 md:max-h-[calc(100vh-9rem)] md:overflow-y-auto">
                {showToc && (
                  <nav
                    aria-label={t('HELP.TOC_TITLE')}
                    className="mb-10 hidden md:block"
                  >
                    <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                      {t('HELP.TOC_TITLE')}
                    </p>
                    {tocList}
                  </nav>
                )}
                <div className="border-t border-ink-100/40 pt-6">
                  <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                    {t('HELP.HOME_CONTACT_EYEBROW')}
                  </p>
                  <h3 className="mt-3 font-display text-[22px] leading-[1.2] font-medium tracking-[-0.005em] text-ink-700 md:text-[26px]">
                    {`${t('HELP.ARTICLE_SIDEBAR_TITLE_PRIMARY')} `}
                    <span className="font-display text-blue-500 italic">
                      {t('HELP.ARTICLE_SIDEBAR_TITLE_ITALIC')}
                    </span>
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                    {t('HELP.ARTICLE_SIDEBAR_DESC')}
                  </p>
                  <div className="mt-5 flex flex-col gap-3">
                    <a
                      href="https://info.spotme.mx/meetings/spotme/contact"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-center font-display text-[14px] font-medium text-white-500 transition-all hover:bg-blue-600 active:scale-[0.98]"
                    >
                      {t('HELP.ARTICLE_AGENDA_CALL')}
                    </a>
                    <a
                      href={buildWhatsappUrl(
                        appendCityHint(
                          t('HELP.ARTICLE_WHATSAPP_MSG', { title }),
                          cityHintLine
                        )
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full border border-ink-700/30 px-6 py-3 text-center font-display text-[14px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
                    >
                      {t('HELP.ARTICLE_WHATSAPP')}
                    </a>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  )
}

export default React.memo(Article)
